import { RefObject, useEffect, useRef, useState } from 'react';

interface IntersectionObserverInit {
    root?: Element | Document | null;
    rootMargin?: string;
    threshold?: number | number[];
}

type IntersectionObserverProps = {
    targetRef: RefObject<Element>;
    observerOptions?: IntersectionObserverInit;
} & (
    | {
          intersectingCallback?: never;
          nonIntersectingCallback: () => void;
      }
    | {
          intersectingCallback: () => void;
          nonIntersectingCallback?: never;
      }
    | {
          intersectingCallback: (entry: IntersectionObserverEntry) => void;
          nonIntersectingCallback: () => void;
      }
);

// This hook is used to know when an element is visible in the viewport or not
export function useIntersectionObserver({
    targetRef,
    intersectingCallback,
    nonIntersectingCallback,
    observerOptions,
}: IntersectionObserverProps): RefObject<Element> {
    const intersectingCallbackRef =
        useRef<(entry: IntersectionObserverEntry) => void | undefined>();
    const nonIntersectingCallbackRef = useRef<() => void | undefined>();
    const hasIntersectingCallback = !!intersectingCallback;
    const hasNonIntersectingCallback = !!nonIntersectingCallback;
    // Store the latest callback in a ref to avoid triggering useEffect on every render
    intersectingCallbackRef.current = intersectingCallback;
    nonIntersectingCallbackRef.current = nonIntersectingCallback;

    // Make an IntersectionObserver with the provided callback and options
    const [_observerOptions] = useState(
        observerOptions ?? { rootMargin: '20px' }
    );
    const [observer, setObserver] = useState<
        IntersectionObserver | undefined
    >();
    useEffect(() => {
        setObserver(
            new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && hasIntersectingCallback) {
                        intersectingCallbackRef.current?.(entry);
                    } else if (
                        !entry.isIntersecting &&
                        hasNonIntersectingCallback
                    ) {
                        nonIntersectingCallbackRef.current?.();
                    }
                });
            }, _observerOptions)
        );
    }, [
        intersectingCallbackRef,
        nonIntersectingCallbackRef,
        _observerOptions,
        hasIntersectingCallback,
        hasNonIntersectingCallback,
    ]);

    // Observe the provided element
    useEffect(() => {
        if (observer && targetRef.current) {
            observer.observe(targetRef.current);
        }
        return () => {
            observer?.disconnect();
        };
    }, [observer, targetRef]);

    return targetRef;
}
